import React, { useEffect, useState } from 'react';
import theme from '../../common/color/theme';
import { CenterLayout, HorizontalLayout, useResponsivePadding, VerticalLayout } from '../../common/common-layout';
import { Body1, Body2, Date2, H3, Point2, Point3, TitleNormalBold } from '../../common/common-text';
import BTBox from '../../common/components/BTBox';
import BTButton from '../../common/components/BTButton';
import { BTDropdown } from '../../common/components/BTDropdown';
import BTImageInput from '../../common/components/BTImageInput';
import { BTOutlineButton } from '../../common/components/BTOutlineButton';
import { RecruitPost } from '../../common/data/RecruitData';
import { fetchRecruits, fetchTabManagers } from '../../common/firebase/FirestoreService';
import { pxToRem } from '../../common/utils/ConvertUtils';
import '../../css/bbpage.css';
import { useDeviceType } from '../../hooks/useDeviceType';

type RecruitSection1rops = {
  section1Height: number;
};

const options = [
  { label: "전체", value: "전체" },
  { label: "상시", value: "상시" },
  { label: "계열사", value: "계열사" },
  { label: "기타", value: "기타" },
];

const RecruitSection1 = ({ section1Height }: RecruitSection1rops) => {
  const { horizontal: padding } = useResponsivePadding();
  const [searchText, setSearchText] = useState('');
  const [filteredPosts, setFilteredPosts] = useState<RecruitPost[]>([]);
  const [visibleCount, setVisibleCount] = useState(10);
  const [selectedPost, setSelectedPost] = useState<RecruitPost | null>(null);
  const [scrollPosY, setScrollPosY] = useState(0);
  const { isMobile } = useDeviceType();
  const [recruitPosts, setRecruitPosts] = useState<RecruitPost[]>([]);
  const [detailText, setDetailText] = useState('');
  const [currentCategory, setCurrentCategory] = useState('전체');

  // 뒤로가기 처리
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (selectedPost) {
        // 히스토리 back 막고 custom 동작 실행
        event.preventDefault(); // 일부 브라우저에서 무시될 수 있음
        setSelectedPost(null);
        window.scrollTo({ top: scrollPosY, behavior: 'instant' });

        // 히스토리 "앞으로 가기" 방지
        window.history.pushState(null, '', window.location.href);
      }
    };

    window.addEventListener('popstate', handlePopState);

    // clean up
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [selectedPost, scrollPosY]);

  const getDefaultJobCategories = () => {
    return [
      { label: '전체', active: true },
      { label: '상시', active: false },
      { label: '계열사', active: false },
      { label: '기타', active: false },
    ];
  };

  const [jobCategories, setJobCategories] = useState(getDefaultJobCategories());

  const handleCategoryClick = (index: number) => {
    const updatedCategories = jobCategories.map((item, i) => ({
      ...item,
      active: i === index,
    }));
    setJobCategories(updatedCategories);

    const selectedLabel = updatedCategories[index].label;
    setCurrentCategory(selectedLabel);

    const filtered = recruitPosts.filter((post: RecruitPost) => {
      const inCategory = selectedLabel === '전체' || post.location === selectedLabel;
      const matchesSearch = (
        post.title +
        post.location +
        post.experience +
        post.type +
        post.dateRange
      ).toLowerCase().includes(searchText.trim().toLowerCase());

      return inCategory && matchesSearch;
    });

    setFilteredPosts(filtered);
    setVisibleCount(Math.min(10, filtered.length));
  };

  const filterPosts = (category: string, text: string) => {
    const filtered = recruitPosts.filter((post: RecruitPost) => {
      const searchable = [
        post.title,
        post.location,
        post.experience,
        post.type,
        post.dateRange,
      ].join(' ').toLowerCase();

      const matchesText = searchable.includes(text.trim().toLowerCase());
      const inCategory = category === '전체' || post.location === category;

      return matchesText && inCategory;
    });

    setFilteredPosts(filtered);
    setVisibleCount(Math.min(10, filtered.length));
  };

  const handleCategoryChange = (category: string) => {
    setCurrentCategory(category);
    filterPosts(category, searchText);
  };

  const handleSearch = () => {
    const filtered = recruitPosts.filter((post: RecruitPost) => {
      const searchableString = [
        post.title,
        post.location,
        post.experience,
        post.type,
        post.dateRange,
      ].join(' ').toLowerCase();

      return searchableString.includes(searchText.trim().toLowerCase()) && (currentCategory === '전체' || post.location === currentCategory);
    });

    setFilteredPosts(filtered);
    setVisibleCount(10);
  };

  const handleShowMore = () => {
    setVisibleCount(prev => prev + 10);
  };

  const visiblePosts = filteredPosts.slice(0, visibleCount);
  const hasMore = visibleCount < filteredPosts.length;

  const handleToList = () => {
    setSelectedPost(null);
    setTimeout(() => {
      window.scrollTo({ top: scrollPosY, behavior: 'instant' });
    }, 50);
  };

  const detailContent = () => {
    return (
      <div
        style={{
          color: 'black',
          maxWidth: '100%',
          overflowX: 'auto',
          wordWrap: 'break-word',
          wordBreak: 'break-word',
        }}
        dangerouslySetInnerHTML={{
          __html: `<style>
            img {
              max-width: 100%;
              height: auto;
            }
          </style>${detailText}`,
        }}
      />
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      const tab = await fetchTabManagers('recruit');
      const tabCodes = tab.map(item => item.tabCode);
      const recruits = await fetchRecruits(tabCodes);
      const allData: RecruitPost[] = recruits.map((recruit) => {
        const today = new Date();
        let dday = '';

        if (recruit.recruitEndDate) {
          const diffTime = recruit.recruitEndDate.getTime() - today.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          dday = diffDays >= 0 ? `D-${diffDays}` : '마감';
        } else {
          dday = '상시채용';
        }

        return {
          title: recruit.jobTitle,
          location: tab.find((item) => item.tabCode === recruit.jobType)?.tabName ?? '',
          experience: '경력', // 필요 시 recruit에 따라 조건 처리 가능
          type: '정규',       // 필요 시 recruit에서 조건 분기 처리 가능
          dateRange: recruit.recruitEndDate
            ? `${recruit.recruitStartDate.toISOString().slice(0, 10)} ~ ${recruit.recruitEndDate.toISOString().slice(0, 10)}`
            : `${recruit.recruitStartDate.toISOString().slice(0, 10)} ~ 채용시까지`,
          content: recruit.contents,
          attachedFiles: recruit.attachedFiles,
          dday,
        };
      });

      setRecruitPosts(allData);
    };

    fetchData();
  }, []);

  useEffect(() => {
    setFilteredPosts(recruitPosts);
    setVisibleCount(Math.min(10, recruitPosts.length));
    setJobCategories(getDefaultJobCategories());
  }, [recruitPosts]);

  return (
    <VerticalLayout paddingLeft={padding} paddingRight={padding} style={{ background: 'white' }}>
      <BTBox height={isMobile ? 40 : `${pxToRem(108)}`} />
      <HorizontalLayout
        style={{
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? '4px' : '0',
        }}
      >
        <H3>Join us</H3>
        <VerticalLayout style={{ flex: 1 }} />
        <Body1 style={{ opacity: 0.5 }}>비비테크와 함께 할 동료를 기다립니다.</Body1>
      </HorizontalLayout>

      <BTBox height={isMobile ? 32 : `${pxToRem(60)}`} />
      {!selectedPost && (
        <>
          {!isMobile ? (
            <>
              <HorizontalLayout>
                {jobCategories.map((item, index) => (
                  <React.Fragment key={item.label}>
                    <HorizontalLayout wrapContents={true} style={{ alignItems: 'center' }}>
                      <Point2
                        style={{ color: item.active ? 'black' : '#979797', cursor: 'pointer' }}
                        onClick={() => handleCategoryClick(index)}
                        data-analytics-name={`탭_${item.label}`}
                      >
                        {item.label}
                      </Point2>
                      <BTBox width={30} />
                      {(index < jobCategories.length - 1) && <VerticalLayout height={isMobile ? 12 : 20} width={1} backgroundColor='black' style={{ opacity: 0.2 }} />}
                    </HorizontalLayout>
                    {index < jobCategories.length - 1 && <BTBox width={30} />}
                  </React.Fragment>
                ))}
              </HorizontalLayout>
              <BTBox height={10} />
              <VerticalLayout height={1} backgroundColor='black' style={{ opacity: 0.4 }} />
              <BTBox height={50} />
            </>
          ) : (
            <>
              <div style={{ width: '100%' }}>
                <BTDropdown
                  options={options}
                  value={currentCategory}
                  onChange={handleCategoryChange}
                  placeholder="직무를 선택하세요"
                  analyticsName="탭"
                />
              </div>
            </>
          )}

          {isMobile ? (
            <VerticalLayout
              style={{
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'stretch' : 'center',
              }}
            >
              <BTBox height={8} />
              <div style={{
                width: "100%",
                height: "40px",
                border: "1px solid #ccc",     // ✅ 테두리 추가
                borderRadius: "6px",          // ✅ 둥근 모서리 적용
                overflow: "hidden",           // ✅ 내부 인풋과 테두리 깔끔하게 맞추기
                display: "flex",
                alignItems: "center",
                backgroundColor: "white",     // ✅ 드롭다운 느낌
                boxSizing: "border-box",
              }}>
                <BTImageInput
                  hint='Search Jobs'
                  value={searchText}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') handleSearch();
                  }}
                />
              </div>
              <BTBox height={24} />
              <Point2 style={{ color: theme.colors.BIBI }}>
                {filteredPosts.length} 개의 채용공고가 있습니다
              </Point2>
            </VerticalLayout>
          ) : (
            <HorizontalLayout
              style={{
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'stretch' : 'center',
              }}
            >
              <Point2 style={{ color: theme.colors.BIBI }}>
                {filteredPosts.length} 개의 채용공고가 있습니다
              </Point2>
              <VerticalLayout style={{ flex: 1 }} />
              <div style={{ width: isMobile ? '100%' : '250px', height: isMobile ? '40px' : undefined, paddingRight: padding }}>
                <BTImageInput
                  hint='Search Jobs'
                  value={searchText}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') handleSearch();
                  }}
                />
              </div>
            </HorizontalLayout>
          )}

          <BTBox height={isMobile ? 17 : 10} />
          <VerticalLayout height={1} backgroundColor='black' style={{ opacity: isMobile ? '1' : '0.2' }} />

          {filteredPosts.length > 0 ? (
            <>
              {visiblePosts.map((post: RecruitPost, index: number) => (
                <React.Fragment key={index}>
                  {isMobile ? (
                    <HorizontalLayout
                      paddingTop={`20px`}
                      paddingBottom={`20px`}
                      style={{ alignItems: 'center', cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedPost(post);
                        setDetailText(post.content);
                        setScrollPosY(window.scrollY);
                        window.history.pushState(null, '', window.location.href);
                        window.scrollTo({ top: section1Height, behavior: 'instant' });
                      }}
                      data-analytics-name={`채용공고_${post.title}`}
                    >
                      <VerticalLayout style={{ flex: 1 }}>
                        <BTButton textColor='white' color={post.dday ? theme.colors.BIBI : '#BEBEBE'} sx={{
                          fontSize: `9px`,
                          width: `50px`,
                          height: `20px`,
                          pointerEvents: 'none',
                          cursor: 'default',
                          padding: 0,
                          minWidth: "unset",
                          borderRadius: '5px'
                        }}>{post.dday ? '접수중' : '접수마감'}</BTButton>
                        <BTBox height={8} />
                        <Body2 style={{ fontSize: '15px' }}>
                          {post.title.split('\n').map((line, i) => (
                            <React.Fragment key={i}>
                              {line}
                              <br />
                            </React.Fragment>
                          ))}
                        </Body2>
                        <BTBox width={4} />
                        <HorizontalLayout style={{ alignItems: 'center' }}>
                          <Point3 style={{ color: '#464646' }}>{post.dateRange}</Point3>
                          <BTBox width={15} />
                          {post.dday && (
                            <>
                              <HorizontalLayout width={1} height={16} backgroundColor="#00000033" />
                              <BTBox width={15} />
                              <TitleNormalBold style={{ fontSize: '13px' }} color={theme.colors.BIBI}>{post.dday}</TitleNormalBold>
                            </>
                          )}
                        </HorizontalLayout>
                      </VerticalLayout>
                    </HorizontalLayout>

                  ) : (
                    <HorizontalLayout
                      paddingTop={`${pxToRem(40)}`}
                      paddingBottom={`${pxToRem(40)}`}
                      style={{ alignItems: 'center', cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedPost(post);
                        setDetailText(post.content);
                        setScrollPosY(window.scrollY);
                        window.history.pushState(null, '', window.location.href);
                        window.scrollTo({ top: section1Height, behavior: 'instant' });
                      }}
                      data-analytics-name={`채용공고_${post.title}`}
                    >
                      <VerticalLayout style={{ flex: 1 }}>
                        <Body2>
                          {post.title.split('\n').map((line, i) => (
                            <React.Fragment key={i}>
                              {line}
                              <br />
                            </React.Fragment>
                          ))}
                        </Body2>
                        <BTBox width={4} />
                        <HorizontalLayout style={{ alignItems: 'center' }}>
                          <Date2 style={{ color: '#464646' }}>{post.dateRange}</Date2>
                          <BTBox width={15} />
                          {post.dday && (
                            <>
                              <HorizontalLayout width={1} height={16} backgroundColor="#00000033" />
                              <BTBox width={15} />
                              <TitleNormalBold color={theme.colors.BIBI}>{post.dday}</TitleNormalBold>
                            </>
                          )}
                        </HorizontalLayout>
                      </VerticalLayout>
                      <VerticalLayout width={100} />
                      <BTButton textColor='white' color={post.dday ? theme.colors.BIBI : '#BEBEBE'} sx={{
                        fontSize: `${pxToRem(20)}`,
                        width: `${pxToRem(100)}`,
                        height: `${pxToRem(60)}`,
                        pointerEvents: 'none',
                        cursor: 'default',
                      }}>{post.dday ? '접수중' : '접수마감'}</BTButton>
                    </HorizontalLayout>
                  )}
                  <VerticalLayout height={1} backgroundColor="black" style={{ opacity: '0.2' }} />
                </React.Fragment>
              ))}
            </>
          ) : (
            <VerticalLayout>
              <BTBox height={isMobile ? 40 : pxToRem(254)} />
              <CenterLayout>
                <Body1 style={{ textAlign: 'center', opacity: 0.5 }}>
                  현재 진행 중인 채용 공고가 없습니다.<br />추후 새로운 채용이 열리면 본 게시판을 통해 안내드리겠습니다.
                </Body1>
              </CenterLayout>
              <BTBox height={isMobile ? 130 : pxToRem(584)} />
            </VerticalLayout>
          )}

          {hasMore && (
            <>
              <BTBox height={isMobile ? '0px' : `${pxToRem(176)}`} />
              <CenterLayout>
                <div style={{ width: isMobile ? '100%' : '335px' }}>
                  <BTOutlineButton
                    color="#464646"
                    borderColor="#979797"
                    centered={true}
                    onClick={handleShowMore}
                    hoverBackgroundColor={theme.colors.BIBI}
                    hoverTextDecoration='none'
                    hoverBorderColor='white'
                    hoverColor='white'
                    borderWidth={1}
                    height={isMobile ? 60 : pxToRem(102)}
                    fontSize={isMobile ? 15 : pxToRem(28)}
                    data-analytics-name="채용공고_더보기"
                  >
                    더보기
                  </BTOutlineButton>
                </div>
              </CenterLayout>
            </>
          )}
        </>
      )}

      {/* 상세 화면 */}
      {selectedPost && (
        <>
          <VerticalLayout height={isMobile ? 1 : 2} backgroundColor='black' style={{ opacity: 0.4 }} />
          <HorizontalLayout
            paddingTop={isMobile ? 16 : 40}
            paddingBottom={isMobile ? 18 : 38}
            style={{ alignItems: 'center' }}
          >
            <VerticalLayout style={{ flex: 1 }}>
              <Body2 style={{
                ...isMobile ? { fontSize: '18px', fontWeight: 700 } : {}
              }}>
                {selectedPost.title.split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </Body2>
              {isMobile ? <BTBox height={16} /> : <BTBox width={4} />}
              <HorizontalLayout style={{ alignItems: 'center' }}>
                <HorizontalLayout style={{ alignItems: 'center' }}>
                  <Point3 style={{ color: '#464646' }}>{selectedPost.dateRange}</Point3>
                  <BTBox width={15} />
                  {selectedPost.dday && (
                    <>
                      <HorizontalLayout width={1} height={isMobile ? 12 : 16} backgroundColor="#00000033" />
                      <BTBox width={15} />
                      <TitleNormalBold style={{
                        ...isMobile ? { fontSize: '13px' } : {}
                      }} color={theme.colors.BIBI}>{selectedPost.dday}</TitleNormalBold>
                    </>
                  )}
                </HorizontalLayout>
              </HorizontalLayout>
            </VerticalLayout>
          </HorizontalLayout>
          <VerticalLayout height={1} backgroundColor="black" style={{ opacity: '0.2' }} />

          {/* 상세 내용 */}
          {detailContent()}

          {isMobile ? (
            <>
              <BTBox height={50} />
            </>
          ) : (
            <>
              <BTBox height={pxToRem(50)} />
              <VerticalLayout height={1} backgroundColor="black" style={{ opacity: '0.2' }} />
              <BTBox height={pxToRem(131)} />
            </>
          )}
          <CenterLayout style={{ width: isMobile ? '100%' : undefined }}>
            <HorizontalLayout
              wrapContents={true}
              style={{
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '8px' : undefined, // 모바일에서는 버튼 사이 간격 줌
                alignItems: 'center',
                width: isMobile ? '100%' : undefined
              }}
            >
              {/* <BTButton
                width={isMobile ? '100%' : `${pxToRem(359)}`}
                height={isMobile ? '62px' : `${pxToRem(102)}`}
                color={theme.colors.BIBI}
                textColor="white"
                sx={{
                  fontWeight: 700,
                  fontSize: `${pxToRem(28)}`,
                }}
                onClick={handleSave}
              >
                지원하기
              </BTButton>
              <BTBox width={30} /> */}
              <BTButton
                width={isMobile ? '100%' : `${pxToRem(359)}`}
                height={isMobile ? '60px' : `${pxToRem(102)}`}
                color='#E1E1E1'
                textColor='#707070'
                borderColor='#C2C2C2'
                hoverBorderColor='#979797'
                hoverColor='#F5F5F5'
                hoverTextColor='#464646'
                sx={{
                  fontWeight: 700,
                  fontSize: isMobile ? '15px' : `${pxToRem(28)}`,
                }}
                onClick={handleToList} data-analytics-name="채용공고_목록보기"
              >
                목록보기
              </BTButton>
            </HorizontalLayout>
          </CenterLayout>
        </>
      )}
      <BTBox height={isMobile ? 149 : 300} />
    </VerticalLayout>
  );
};

export default RecruitSection1;
